import React, { useEffect, useRef, useState } from 'react';
import { RateioAjusteUc } from '../../../types';
import {
  ajusteAberto,
  conferirAjuste,
  DIFERENCA_RELEVANTE,
  DIVERGENCIAS,
  formatarKwh,
  formatarPercentRateio,
  STATUS_AJUSTE,
  type LinhaSaldoUc,
} from './saldosUcs';
import { FAIXA_META, TENDENCIA_META, nomeCurtoUsina } from './saldosUcsVisual';

interface TabelaSaldosUcsProps {
  /** Já filtradas e ordenadas. */
  linhas: LinhaSaldoUc[];
  mesRateio: string;
  ajustesDaLinha: (l: LinhaSaldoUc) => RateioAjusteUc[];
  onAbrir: (l: LinhaSaldoUc) => void;
  onAjustar: (l: LinhaSaldoUc) => void;
  /** Muda quando o filtro, a ordem ou o mês mudam: a lista volta ao topo. Registrar um ajuste não muda. */
  chaveReinicio: string;
}

const POR_PAGINA = 60;

/**
 * Lista de UCs com rolagem própria: os títulos das colunas ficam presos no topo
 * enquanto o conteúdo rola, e as linhas vão sendo carregadas conforme chega no
 * fim (1.000+ linhas de uma vez travariam a tela). Quem usa coloca a barra de
 * filtros FORA deste componente, e ela também não rola.
 */
const TabelaSaldosUcs: React.FC<TabelaSaldosUcsProps> = ({ linhas, mesRateio, ajustesDaLinha, onAbrir, onAjustar, chaveReinicio }) => {
  const [limite, setLimite] = useState(POR_PAGINA);
  const rolagemRef = useRef<HTMLDivElement>(null);
  const fimRef = useRef<HTMLDivElement>(null);

  // Filtro, ordem ou mês mudou: volta para o topo da lista.
  useEffect(() => {
    setLimite(POR_PAGINA);
    if (rolagemRef.current) rolagemRef.current.scrollTop = 0;
  }, [chaveReinicio]);

  useEffect(() => {
    const raiz = rolagemRef.current;
    const alvo = fimRef.current;
    if (!raiz || !alvo || limite >= linhas.length) return;
    const obs = new IntersectionObserver(
      entradas => {
        if (entradas.some(e => e.isIntersecting)) setLimite(v => Math.min(v + POR_PAGINA, linhas.length));
      },
      { root: raiz, rootMargin: '300px' }
    );
    obs.observe(alvo);
    return () => obs.disconnect();
  }, [limite, linhas.length]);

  const visiveis = linhas.slice(0, limite);
  const th = 'py-2.5 px-3 font-semibold bg-gray-50 dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700';

  return (
    <div className="flex flex-col flex-1 min-h-0">
      <div ref={rolagemRef} className="flex-1 min-h-0 overflow-auto">
        <table className="w-full text-sm min-w-[980px] border-separate border-spacing-0">
          <thead className="sticky top-0 z-10">
            <tr className="text-left text-[11px] uppercase tracking-wide text-gray-500 dark:text-gray-400">
              <th className={`${th} px-4`}>UC / cliente</th>
              <th className={th}>Status</th>
              <th className={th}>Usina</th>
              <th className={`${th} text-right`}>% vigente</th>
              <th className={`${th} text-right`}>% sugerido</th>
              <th className={`${th} text-right`}>Saldo</th>
              <th className={th}>Cobertura</th>
              <th className={th}>Ajuste</th>
            </tr>
          </thead>
          <tbody>
            {visiveis.length === 0 ? (
              <tr>
                <td colSpan={8} className="py-10 text-center text-sm text-gray-400">Nenhuma UC com esses filtros.</td>
              </tr>
            ) : (
              visiveis.map(l => {
                const aberto = ajustesDaLinha(l).find(ajusteAberto);
                const conferido = aberto ? conferirAjuste(aberto, l, mesRateio) : null;
                const faixa = l.faixa ? FAIXA_META[l.faixa] : null;
                const diferenca = l.diferencaSugestao;
                const td = 'py-2.5 px-3 border-b border-gray-100 dark:border-gray-700/70';
                return (
                  <tr key={l.itemId} onClick={() => onAbrir(l)} className="cursor-pointer hover:bg-gray-50/80 dark:hover:bg-gray-700/40 transition-colors">
                    <td className={`${td} px-4 max-w-[260px]`}>
                      <p className="font-semibold text-gray-900 dark:text-white truncate" title={l.nome}>{l.nome || '—'}</p>
                      <p className="text-[11px] text-gray-400 tabular-nums">
                        {l.ucNumero}
                        {l.ucNovo && l.ucNovo !== l.ucNumero ? ` · ${l.ucNovo}` : ''}
                      </p>
                      {l.divergencias.length > 0 ? (
                        <div className="flex flex-wrap gap-1 mt-1">
                          {l.divergencias.map(d => (
                            <span key={d} title={DIVERGENCIAS[d].descricao} className="px-1.5 py-0.5 rounded text-[10px] font-semibold bg-amber-50 text-amber-700 dark:bg-amber-900/20 dark:text-amber-400">
                              {DIVERGENCIAS[d].rotulo}
                            </span>
                          ))}
                        </div>
                      ) : null}
                    </td>
                    <td className={`${td} text-xs text-gray-700 dark:text-gray-200 whitespace-nowrap`}>
                      {l.statusPlanilha || '—'}
                      {l.statusCadastro && l.statusCadastro !== l.statusPlanilha ? (
                        <span className="block text-[11px] text-gray-400">cadastro: {l.statusCadastro}</span>
                      ) : null}
                    </td>
                    <td className={`${td} text-xs text-gray-700 dark:text-gray-200 max-w-[170px]`}>
                      <span className="block truncate" title={l.ugNome || l.ugNumero || undefined}>
                        {l.ugNome ? nomeCurtoUsina(l.ugNome) : l.ugNumero || '—'}
                      </span>
                    </td>
                    <td className={`${td} text-right tabular-nums text-gray-800 dark:text-gray-100`}>
                      {formatarPercentRateio(l.percentVigente)}
                      {l.percentAnterior != null && l.percentVigente != null && Math.abs(l.percentAnterior - l.percentVigente) >= 0.00005 ? (
                        <span className="block text-[11px] text-gray-400">antes {formatarPercentRateio(l.percentAnterior)}</span>
                      ) : null}
                    </td>
                    <td className={`${td} text-right tabular-nums`}>
                      {l.sugestao?.percent != null ? (
                        <>
                          <span className="text-gray-800 dark:text-gray-100">{formatarPercentRateio(l.sugestao.percent)}</span>
                          {diferenca != null && Math.abs(diferenca) >= DIFERENCA_RELEVANTE ? (
                            <span className={`block text-[11px] font-semibold ${diferenca > 0 ? 'text-red-600 dark:text-red-400' : 'text-blue-600 dark:text-blue-400'}`}>
                              {diferenca > 0 ? '▲ subir' : '▼ reduzir'} {formatarPercentRateio(Math.abs(diferenca))}
                            </span>
                          ) : null}
                        </>
                      ) : (
                        <span className="text-gray-300 dark:text-gray-600" title={l.sugestao?.motivo}>—</span>
                      )}
                    </td>
                    <td className={`${td} text-right tabular-nums text-gray-800 dark:text-gray-100`}>
                      {l.saldo ? `${formatarKwh(l.saldo.kwh)} kWh` : <span className="text-gray-300 dark:text-gray-600">—</span>}
                      {l.saldo ? (
                        <span
                          className="block text-[11px] text-gray-400"
                          title={l.ultimaFatura && l.ultimaFatura.mes !== l.saldo.mes ? `A fatura de ${l.ultimaFatura.mes} veio sem saldo lido` : undefined}
                        >
                          {l.saldo.mes}
                          {l.ultimaFatura && l.ultimaFatura.mes !== l.saldo.mes ? ' *' : ''}
                        </span>
                      ) : l.ultimaFatura ? (
                        <span className="block text-[11px] text-gray-400">sem saldo lido</span>
                      ) : null}
                    </td>
                    <td className={`${td} whitespace-nowrap`}>
                      {faixa && l.cobertura ? (
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[11px] font-bold ${faixa.chip}`} title={faixa.descricao}>
                          {l.cobertura.coberturaMeses.toLocaleString('pt-BR', { maximumFractionDigits: 1 })} {l.cobertura.coberturaMeses === 1 ? 'mês' : 'meses'}
                        </span>
                      ) : (
                        <span className="text-gray-300 dark:text-gray-600 text-xs">—</span>
                      )}
                      {l.tendencia ? (
                        <span className={`ml-1.5 text-xs font-bold ${TENDENCIA_META[l.tendencia].cor}`} title={`Saldo ${TENDENCIA_META[l.tendencia].rotulo.toLowerCase()}`}>
                          {TENDENCIA_META[l.tendencia].simbolo}
                        </span>
                      ) : null}
                    </td>
                    <td className={td} onClick={e => e.stopPropagation()}>
                      {aberto ? (
                        <button
                          onClick={() => onAbrir(l)}
                          className={`px-2 py-0.5 rounded-md text-[11px] font-bold ${
                            conferido === 'confirmado'
                              ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400'
                              : conferido === 'nao_confirmado'
                                ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
                                : 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400'
                          }`}
                        >
                          {conferido === 'confirmado' ? 'Veio no rateio' : conferido === 'nao_confirmado' ? 'Não veio' : STATUS_AJUSTE[aberto.status]}
                        </button>
                      ) : (
                        <button
                          onClick={() => onAjustar(l)}
                          className="px-2 py-0.5 rounded-md text-[11px] font-semibold border border-gray-200 dark:border-slate-700 text-gray-500 dark:text-slate-400 hover:bg-gray-50 dark:hover:bg-slate-800"
                        >
                          Ajustar
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
        <div ref={fimRef} className="h-1" aria-hidden="true" />
      </div>
      <div className="flex-shrink-0 px-4 py-2 border-t border-gray-100 dark:border-gray-700 text-xs text-gray-500 dark:text-gray-400">
        {linhas.length === 0
          ? 'Nenhuma UC'
          : visiveis.length < linhas.length
            ? `Mostrando ${visiveis.length.toLocaleString('pt-BR')} de ${linhas.length.toLocaleString('pt-BR')} UCs — role para carregar mais`
            : `Mostrando ${linhas.length.toLocaleString('pt-BR')} de ${linhas.length.toLocaleString('pt-BR')} UCs`}
      </div>
    </div>
  );
};

export default TabelaSaldosUcs;
